import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Star, MapPin, Clock, Shield, Award, User as UserIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { getProviderProfile } from '../services/api';
import { getServiceById } from '../data/services';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ProviderProfile = () => {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    fetchProfile();
  }, [providerId, token, navigate]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await getProviderProfile(providerId);
      setProvider(response.data.provider);
      setReviews(response.data.reviews);
      setServices(response.data.services);
    } catch (error) {
      navigate('/search');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!provider) return null;

  const rate = provider.hourly_rate || 1000;
  const gstPerHour = Math.round(rate * 0.18);

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="provider-profile-page">
      <Header />

      <div className="max-w-4xl mx-auto px-6 pt-28 pb-12">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Search
        </button>

        {/* Provider Header Card */}
        <div className="premium-card p-8 mb-8">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="w-24 h-24 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center text-white text-4xl font-bold flex-shrink-0">
              {provider.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900" data-testid="provider-name">{provider.name}</h1>
              <div className="flex flex-wrap items-center gap-4 mt-3">
                <div className="flex items-center gap-1 text-gray-500">
                  <MapPin className="w-4 h-4" />
                  <span>{provider.city || 'Location not set'} {provider.pincode && `- ${provider.pincode}`}</span>
                </div>
                {provider.gender && (
                  <span className="text-gray-500 capitalize">{provider.gender}</span>
                )}
                {provider.age && (
                  <span className="text-gray-500">{provider.age} years</span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-1">
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  <span className="font-bold text-lg">{provider.avg_rating || 'New'}</span>
                  {provider.total_reviews > 0 && (
                    <span className="text-gray-400 text-sm">({provider.total_reviews} reviews)</span>
                  )}
                </div>
                <div className="flex items-center gap-1 text-emerald-600">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm font-medium">Verified</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-amber-600" data-testid="provider-rate">Rs.{rate}</p>
              <p className="text-sm text-gray-500">per hour + 18% GST</p>
              <p className="text-xs text-gray-400 mt-1">Total: Rs.{rate + gstPerHour}/hr</p>
            </div>
          </div>
          {provider.bio && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <h3 className="text-sm font-medium text-gray-500 mb-2">About</h3>
              <p className="text-gray-700">{provider.bio}</p>
            </div>
          )}
        </div>

        {/* Services Offered */}
        {services.length > 0 && (
          <div className="premium-card p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Services Offered
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {services.map((s) => {
                const svc = getServiceById(s.id);
                const Icon = svc?.icon;
                return (
                  <div key={s.id} className={`${svc?.color || 'bg-gray-500'} rounded-xl p-3 flex items-center gap-3 text-white`}>
                    {Icon && <Icon className="w-5 h-5" />}
                    <span className="text-sm font-medium">{s.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Reviews */}
        <div className="premium-card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Reviews ({reviews.length})
          </h2>
          {reviews.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <UserIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>No reviews yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="border-b border-gray-100 pb-4 last:border-0" data-testid={`review-${review.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-bold text-gray-600">
                        {review.hirer_name?.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-gray-900">{review.hirer_name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} className={`w-4 h-4 ${star <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">{review.review}</p>
                  <p className="text-gray-400 text-xs mt-2">
                    {review.service_name} | {new Date(review.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Book Now CTA */}
        <div className="mt-8 text-center">
          <Button 
            onClick={() => navigate('/search')}
            className="btn-premium px-8 py-3 text-lg"
            data-testid="book-now-btn"
          >
            Book This Provider
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProviderProfile;
